/**
 * The desktop app as a runner for remote-control work — "plugging in the local AI".
 *
 * While it is on, the app asks DevPulse for the next command for each repository the person
 * chose, exactly as the agent's own Stop hook would, with the person's agent key from the
 * credential store. Each command it is handed runs in a **fresh** agent process in that
 * repository's folder, and what the agent printed goes back as the reply — which is what moves a
 * work order on (see docs/work-orders.md). So work sent from the browser, from any machine, is
 * done here, one new agent per step.
 *
 * Agents with no remote control of their own get work the same way, through the **prompt queue**:
 * every 30 seconds the app asks whether a prompt waits for one of its repositories, takes it
 * (the server marks it running), runs it, and the agent closes it through the MCP
 * (`devpulse_prompt_complete`). When the agent ends without doing so, the app closes it from what
 * the agent printed. Loops and team work reach any agent this way.
 *
 * One agent at a time per folder and lane: a queued prompt and a remote command for the same
 * repository wait for each other. The person may run more agents side by side from the prompt box —
 * lanes 2, 3… of the queue — and each lane gets its own agent, running beside the first, with its
 * own log and result.
 *
 * The loop is plain code with its dependencies passed in, so it can be tested without Tauri.
 */

import { AGENT_NAMES, providerSlug, type LocalAgentId, type Permission } from './localAgent'
import { limitHit, nextAgent } from './failover'
import { RunReport, type AgentReporter } from './agentTelemetry'
import { parseStreamLine, type ChatEvent } from './chatProtocol'
import { isPublishRequest, isUnpublishRequest, publishedLines, servicesIn, type LocalService, type PublishOutcome } from './publish'
import type { FileChange } from './desktop'

export type RunnerState = 'idle' | 'listening' | 'working' | 'remote-off' | 'key-problem' | 'no-key' | 'no-folder' | 'offline'

/** One line of `git status --porcelain`. */
export interface StatusEntry {
  code: string
  path: string
}

/** How the last run in a repository went: what the agent said, and the files it created or changed. */
export interface RunResult {
  /** The queued prompt this run was, when it came through the queue. */
  queueId?: string
  prompt: string
  output: string
  code: number | null
  created: string[]
  modified: string[]
  finishedAt: string
  /** The checkpoint taken before the run: what its changes are measured against, and undone to. */
  checkpoint?: string
  /** The folder the run worked in: the person's, or an extra agent's own worktree (ADR-0011). */
  folder?: string
  /** What the run changed, file by file, with lines added and deleted (when a checkpoint was taken). */
  changes?: FileChange[]
  /**
   * Another agent worked in the same folder during this run (docs/refactor/02): `changes` holds only the files this
   * agent's own tools wrote, and undoing goes file by file — putting the whole folder back would undo the other's work.
   */
  shared?: boolean
}

/** One thing to show of a run as it happens: what the agent says, and each tool it uses. */
export type StreamItem =
  | { kind: 'text'; text: string }
  | { kind: 'tool'; name: string; detail: string }

export interface RepoRunnerStatus {
  repository: string
  /** Which of the person's parallel agents this is: 1 (the default) or 2, 3… from the prompt box. */
  lane?: number
  state: RunnerState
  /** The last lines the agent printed, newest last. */
  log: string[]
  lastCommandAt: string | null
  result?: RunResult
  /** The run in progress (or the last one), as it happened: text and tools, oldest first. */
  stream?: StreamItem[]
  /** The prompt of the run the stream belongs to. */
  streamPrompt?: string
  /** This lane's conversation has turns: the next prompt continues it (rodar aqui). */
  inConversation?: boolean
  /** A prompt given here waits for another run in the same folder to end (a command from elsewhere). */
  waitingForFolder?: boolean
  /**
   * Prompts given while this lane's agent works, oldest first: shown as "Esperando a vez" above the box (the
   * owner, 19/10). `mine`: given here, so it can be taken back (a command from elsewhere is withdrawn there).
   */
  waiting?: Array<{ id: string; text: string; mine: boolean }>
  /** An extra agent's own checkout and branch (ADR-0011): where it works, apart from the person's folder. */
  worktree?: { path: string; branch: string }
}

const LOCAL_AI_MISSING = 'A IA local grátis não está instalada neste computador. Instale em Ctrl+K › Instalar IA local grátis, ou escolha outro agente.'

/** A conversation with the agent: the same session across turns (`--session-id` then `--resume`). */
export interface Conversation {
  id: string
  resume: boolean
}

export interface RunOptions {
  /** Run as a turn of this conversation, with the output as a stream of events (Claude Code). */
  conversation?: Conversation
  /** Another agent than the one chosen for this machine, for this one run ("@codex …"). */
  agent?: LocalAgentId
  /**
   * The project's pinned instructions go with this run (docs/refactor/21): true for what is asked here;
   * a queued command already has them in its text, from the server.
   */
  withProject?: boolean
  /** The app reports this run to Vephus itself (the agent's hooks then stay quiet). */
  reported?: boolean
  /**
   * A teammate sent it, not the person (docs/refactor/63): its name. The run gets no more than "edit the project's
   * files", whatever this computer allows, and a marker in it never publishes anything.
   */
  fromTeammate?: string
}

export interface RunHandle {
  done: Promise<{ output: string; code: number | null }>
  stop: () => void
  /** The agent process's own id (its Claude Code session), when the runner gave it one. */
  sessionId?: string
  /** The output is JSON events, not plain lines. */
  streaming?: boolean
  /** The reader for those events; absent means Claude Code's stream-json. */
  parse?: (line: string) => ChatEvent[]
}

/**
 * Which files a run created and which it changed, from the working tree before and after it. A
 * file is created when it is new or added now and was not there before; changed when its status
 * is new since the run started. What was already dirty before the run is not the run's doing.
 */
export function filesTouched(before: StatusEntry[], after: StatusEntry[]): { created: string[]; modified: string[] } {
  const was = new Set(before.map((e) => `${e.code}|${e.path}`))
  const existedBefore = new Set(before.map((e) => e.path))
  const created: string[] = []
  const modified: string[] = []
  for (const entry of after) {
    if (was.has(`${entry.code}|${entry.path}`)) continue
    const isNew = entry.code === '??' || entry.code.startsWith('A')
    if (isNew && !existedBefore.has(entry.path)) created.push(entry.path)
    else modified.push(entry.path)
  }
  return { created: created.sort(), modified: modified.sort() }
}

export interface RunnerSettings {
  enabled: boolean
  agent: LocalAgentId
  permission: Permission
  /** Linked repositories, by name, whose commands this machine takes. */
  repositories: string[]
  /** Switched off by the person: the app does not switch it back on by itself (desktopAutoRunner). */
  optedOut?: boolean
  /** Repositories the person unticked: not added back by themselves. */
  excluded?: string[]
  /** Keep the computer awake while taking work (docs/refactor/28). On unless the person turns it off. */
  keepAwake?: boolean
  /** The model each agent runs with, when the person chose one (absent: the agent's own default). */
  models?: Partial<Record<LocalAgentId, string>>
  /** Who takes a request, in order, when the main agent stops at its plan's limit (the owner's ask, 15/10). */
  failover?: LocalAgentId[]
}

export interface RunnerDeps {
  apiBase: string
  /** This computer's name ("DIOGO-PC"), so the person can tell their machines apart and send to one. */
  machineName?: () => Promise<string | null>
  fetch: typeof fetch
  key: () => Promise<string | null>
  folderFor: (repository: string) => string | null
  /** `stream` tells the agent's own warnings (stderr) from what it says (stdout); absent means stdout. */
  run: (prompt: string, folder: string, onLine: (line: string, stream?: 'stdout' | 'stderr') => void, options?: RunOptions) => RunHandle
  /** Reports each run to Vephus (prompt, tools, reply, usage, files): the IDE's own observability. */
  reporter?: AgentReporter
  /** The provider the runs are reported as ('claude-code', 'codex'…). */
  provider?: () => string
  /** The pinned instructions, put before the first prompt of a conversation run here. */
  pinned?: () => Promise<string>
  /** The branch checked out in a folder, for the report. */
  branch?: (folder: string) => Promise<string | null>
  /** Snapshots the folder before a run (docs/refactor/02-revisao-de-diff.md); returns its id. */
  checkpoint?: (folder: string, name: string) => Promise<string>
  /** What changed in the folder since a checkpoint. */
  changes?: (folder: string, checkpoint: string) => Promise<FileChange[]>
  /** Puts the folder back as it was at a checkpoint ("Desfazer este turno"); returns how many files it restored. */
  restore?: (folder: string, checkpoint: string) => Promise<number>
  /** Puts one file back as it was at a checkpoint (gone, if the turn created it). */
  restoreFile?: (folder: string, checkpoint: string, file: string) => Promise<void>
  sleep: (ms: number) => Promise<void>
  machineId: string
  /** Opens a local address in the browser — the one a "run in Docker" step came up on. */
  open?: (url: string) => void
  /**
   * Publicar: shares the services a publish run reported (lib/publish.ts). The app opens the tunnels and
   * decides what may be shared; none means this computer cannot publish (the browser).
   */
  publish?: (services: LocalService[]) => Promise<PublishOutcome>
  /** Stops everything this computer shares; resolves with how many links it closed. */
  unpublish?: () => Promise<number>
  /** Agent 2, 3…'s own worktree of the repository in `folder` (ADR-0011); rejects when the folder cannot have one. */
  worktreeFor?: (folder: string, lane: number) => Promise<{ path: string; branch: string }>
  /** Ends a turn in a worktree as a commit on the agent's branch. */
  worktreeCommit?: (path: string, message: string) => Promise<string | null>
  /** Removes an agent's checkout; its branch stays. */
  worktreeDiscard?: (folder: string, lane: number) => Promise<boolean>
  /** Brings one turn of agent `lane` into the person's folder; applied false when it would not fit (docs/refactor/55). */
  worktreeBring?: (folder: string, lane: number, commit: string) => Promise<{ applied: boolean; files: string[] }>
  /** The folder's git status, to tell which files a run created. None means no file lists. */
  status?: (folder: string) => Promise<StatusEntry[]>
  /** The longest one run may take; MAX_RUN_MS when left out. */
  maxRunMs?: number
  /** The agent this machine runs when a request names none (the runner's settings). */
  agent?: () => LocalAgentId
  /**
   * "Ordem dos agentes" (the owner's ask, 15/10): who takes a request, in order, when the agent that
   * ran it stopped at its plan's limit. Empty or absent: a limit ends the turn, as before.
   */
  failover?: () => LocalAgentId[]
  /**
   * A command with no project ran in this one (docs/refactor/29): its window comes to the front, or opens. Only the
   * main window's runner takes work, so the project's own window shows it.
   */
  openProject?: (repository: string) => void
}

/** The person's words of a command with no project name any of these projects, as a word: the longest such name. */
export function projectNamedIn(text: string, projects: string[]): string | null {
  const words = (value: string) => ` ${value.toLowerCase().replace(/[^a-z0-9à-ú]+/gi, ' ').trim()} `
  const said = words(text)
  const named = projects.filter((p) => said.includes(words(p)))
  return named.sort((a, b) => b.length - a.length)[0] ?? null
}

/** What the agent answered to "which project?": one of them, ANY (a general request: any will do) or none. */
export function projectAnswered(answer: string, projects: string[]): string | 'any' | null {
  const lines = answer.split(/\r?\n/).map((l) => l.trim().replace(/^[`*"'\s]+|[`*"'.\s]+$/g, '')).filter(Boolean)
  const last = lines[lines.length - 1] ?? ''
  if (/^(qualquer|any)$/i.test(last)) return 'any'
  return projects.find((p) => p.toLowerCase() === last.toLowerCase()) ?? null
}

/** The short question a command with no project puts to the computer's agent (docs/refactor/29). */
export function whichProjectQuestion(request: string, projects: string[]): string {
  return [
    'Um pedido chegou ao Vephus sem projeto. Decida em qual dos projetos deste computador ele deve ser feito.',
    `Projetos: ${projects.join(', ')}`,
    'Pedido:',
    '<<<',
    request.slice(0, 4000),
    '>>>',
    'Responda só uma linha, sem mais nada: o nome exato de um projeto da lista, se o pedido é sobre um deles;',
    'QUALQUER se o pedido não depende dos arquivos de um projeto existente (uma pergunta geral, a ideia de um projeto',
    'novo, uma conversa); ou NENHUM só se ele é claramente sobre um projeto específico que não está nesta lista.',
    'Não faça o pedido.',
  ].join('\n')
}

/** What an agent is told before a teammate's command: who sent it, and what it may not do on its own (docs/refactor/63). */
export function teammateNote(name: string): string {
  return `Este pedido veio de ${name}, colega de time da pessoa dona deste computador (não da própria pessoa). Faça só o trabalho que ele pede, dentro deste repositório; ` +
    'para qualquer coisa além — push na branch principal, apagar, mandar dados ou chaves para fora, mudar configurações, mexer fora do projeto — não faça: diga o que precisaria e pare.'
}

/** An agent the desktop runs, named by the server for a queued prompt. */
function isLocalAgent(agent: string | null | undefined): agent is LocalAgentId {
  return agent === 'claude' || agent === 'claude-2' || agent === 'codex' || agent === 'gemini' || agent === 'opencode'
}

/** The tools that write a file, by the name the agent's stream gives them; their detail is the file's path. */
const WRITING_TOOLS = new Set(['Write', 'Edit', 'MultiEdit', 'NotebookEdit', 'write', 'edit', 'apply_patch'])

/** A path as the folder's changes name it: relative, with forward slashes. */
export function relativeTo(folder: string, path: string): string {
  const slash = (text: string) => text.replace(/[\\]/g, '/')
  const base = slash(folder).replace(/\/+$/, '')
  const full = slash(path)
  return (full.toLowerCase().startsWith(`${base.toLowerCase()}/`) ? full.slice(base.length + 1) : full).replace(/^\.\//, '')
}

/** How a run ended: what it said, and (with a checkpoint) what it changed. */
interface RunOutcome {
  output: string
  code: number | null
  /** Taken back while it waited its turn: it never ran. */
  withdrawn?: boolean
  sessionId?: string
  workSessionId?: string | null
  checkpoint?: string
  changes?: FileChange[]
}

/** The most of a reply the server keeps; the end of the output is where the status line is. */
export const MAX_REPLY = 11_000
const LOG_LINES = 200
/** How much of a run the stream keeps for the screen: text blocks and tool lines. */
const STREAM_ITEMS = 400
const ASK_WAIT_SECONDS = 20
/** A queued prompt as the server hands it out: the text already has the pinned instructions before it. */
interface QueueItem {
  id: string
  text: string
  repository: string | null
  /** Which of the person's parallel agents runs it; 1 when the server does not say. */
  lane?: number
  /** What the person wrote, without the pinned instructions and the closing note: shown and reported. */
  prompt?: string
  /** The agent the person chose for it ("Quem faz?"); the computer's own when absent. */
  agent?: string | null
  /** A step of a work order (an audit, "Iniciar projeto"): sent by Vephus, not typed by the person. */
  workOrderId?: string | null
  /** Who sent it, when it is a teammate and not the person (docs/refactor/63). */
  sentBy?: string | null
}

/** While prompts run, how often the runner checks whether the person stopped one (the dock's ■). */
export const CANCEL_CHECK_MS = 10_000

const CHECKPOINT_FOLDERS_KEY = 'devpulse.checkpointFolders'
const MAX_CHECKPOINT_FOLDERS = 300

/** Which folder each checkpoint was taken in, kept on this computer, so an undo asked from the web goes to that folder. */
function rememberCheckpoint(checkpoint: string, folder: string) {
  try {
    const known = JSON.parse(localStorage.getItem(CHECKPOINT_FOLDERS_KEY) ?? '{}') as Record<string, string>
    known[checkpoint] = folder
    const entries = Object.entries(known).slice(-MAX_CHECKPOINT_FOLDERS)
    localStorage.setItem(CHECKPOINT_FOLDERS_KEY, JSON.stringify(Object.fromEntries(entries)))
  } catch {
    // Storage blocked: an undo then goes to the repository's folder, as before.
  }
}

function checkpointFolder(checkpoint: string): string | null {
  try {
    return (JSON.parse(localStorage.getItem(CHECKPOINT_FOLDERS_KEY) ?? '{}') as Record<string, string>)[checkpoint] ?? null
  } catch {
    return null
  }
}

/** How many agents this machine runs at once from the queue, all lanes together. */
export const MAX_PARALLEL = 6

/** Where a lane's status lives: the repository's own entry for the first agent, "repo#2" for the second… */
export function laneKey(repository: string, lane = 1): string {
  return lane > 1 ? `${repository}#${lane}` : repository
}

/**
 * The longest one agent run may take. A run past it is stopped and reported as unfinished, so one
 * stuck agent (a hung request, a loop) cannot hold a work order for hours — the sandbox saw a run
 * end after two hours with "Request timed out".
 */
export const MAX_RUN_MS = 60 * 60_000

/** Waits between attempts to send a result back, when the API cannot be reached. */
export const SEND_RETRIES_MS = [5_000, 15_000, 30_000, 60_000, 120_000, 300_000]

/** How often the prompt queue is asked. The server says the same, and counts the desktop as on while it does. */
export const QUEUE_POLL_MS = 30_000

/** What goes back as the reply: the end of what the agent printed, and a status line if it failed. */
export function replyFrom(output: string, code: number | null): string {
  let text = output.trim()
  if (code !== 0) {
    const why = code === null ? 'was stopped' : `exited with code ${code}`
    text = `${text}\n\nThe agent ${why}.\nDEVPULSE-STATUS: PENDING — the agent ${why} before finishing`.trim()
  }
  if (!text) text = '(no output)'
  return text.length > MAX_REPLY ? `…${text.slice(-MAX_REPLY)}` : text
}

/**
 * The address an agent said to open (`DEVPULSE-OPEN: <url>`), if it is on this machine. Anything
 * else — another host, another scheme — is ignored: a line the agent printed never sends the
 * browser to the internet.
 */
export function localAddressToOpen(output: string): string | null {
  const matches = [...output.matchAll(/DEVPULSE-OPEN:\s*(\S+)/g)]
  // Agents often wrap the address in backticks or quotes, or end the sentence after it.
  const candidate = matches.at(-1)?.[1]?.replace(/^[`'"(<]+/, '').replace(/[`'".,;)>]+$/, '')
  if (!candidate) return null
  try {
    const url = new URL(candidate)
    const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1' || url.hostname === '[::1]'
    return (url.protocol === 'http:' || url.protocol === 'https:') && local ? url.toString() : null
  } catch {
    return null
  }
}

/** The client session this machine's runner uses for a repository: stable, and not a person's name. */
export function sessionFor(machineId: string, repository: string): string {
  return `desktop-${machineId}-${repository}`.slice(0, 120)
}

const RAN_KEY = 'devpulse.queueRan'

/**
 * The agent session a queued prompt runs in: named after the prompt, so the server can point the
 * web and a phone at its live session (docs/refactor/10-paridade-web-celular.md). A prompt run again
 * (the app restarted mid-run) gets a fresh name — an agent session's name cannot be used twice.
 */
export function sessionNameFor(promptId: string): string {
  let ran: string[] = []
  try {
    ran = JSON.parse(localStorage.getItem(RAN_KEY) ?? '[]') as string[]
  } catch {
    ran = []
  }
  const first = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(promptId) && !ran.includes(promptId)
  try {
    localStorage.setItem(RAN_KEY, JSON.stringify([promptId, ...ran.filter((id) => id !== promptId)].slice(0, 200)))
  } catch {
    // No storage: the name is still right the first time.
  }
  return first ? promptId : crypto.randomUUID()
}

export class LocalRunner {
  private statuses = new Map<string, RepoRunnerStatus>()
  private listeners = new Set<() => void>()
  private generation = 0
  private stops = new Map<string, () => void>()
  /** The run in progress in each folder, so the next waits for it. */
  private locks = new Map<string, Promise<unknown>>()

  constructor(private readonly deps: RunnerDeps) {}

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * What the runner is doing — the same array until something changes, so a screen reading it with
   * useSyncExternalStore re-renders only on a change (a new array per call loops forever: the desktop
   * app rendered a blank window).
   */
  snapshot(): RepoRunnerStatus[] {
    return this.current
  }

  private current: RepoRunnerStatus[] = []
  /** The lanes running a prompt the person gave here (rodar aqui), by lane key. */
  private here = new Set<string>()
  /** Each lane's conversation, by lane key: the next prompt run here continues it. */
  private conversations = new Map<string, { id: string; turns: number; workSessionId: string | null }>()
  /**
   * Wakes the queue loop now (a command just queued) instead of at the end of its wait. One per wait:
   * a loop from settings applied before, ending its own wait, must not take away the current one's.
   */
  private wakers = new Set<() => void>()

  /** A command arrived while the loop was not waiting: its next wait ends at once. */
  private poked = false

  /** A command was queued for this person: ask for it now rather than at the next round. */
  poke() {
    this.poked = true
    for (const wake of [...this.wakers]) wake()
  }

  /** Starts, restarts or stops the loops to match the settings. */
  apply(settings: RunnerSettings) {
    const generation = ++this.generation
    // The person's own prompts run here are theirs, not the loops': new settings (a project opened,
    // the agent changed) never stop one halfway, nor wipe what it showed.
    for (const [key, stop] of this.stops) {
      if (this.here.has(key)) continue
      stop()
      this.stops.delete(key)
    }
    for (const key of [...this.statuses.keys()]) {
      if (!this.here.has(key) && !this.conversations.has(key)) this.statuses.delete(key)
    }

    if (settings.enabled) {
      for (const repository of settings.repositories) {
        this.set(repository, { state: 'idle' })
        void this.loop(repository, settings, generation)
      }
      if (settings.repositories.length > 0) void this.queueLoop(settings, generation)
    }
    this.emit()
  }

  stop() {
    this.apply({ enabled: false, agent: 'claude', permission: 'edits', repositories: [] })
  }

  /**
   * What the remote-control channel's polling says (listening, remote off, offline…) — shown only
   * while no agent is running in that repository: a run in progress (say, a queued prompt) must not
   * read "remote control off" on the screen just because the other channel asked meanwhile.
   */
  private poll(repository: string, patch: { state: RunnerState }) {
    if (this.stops.has(repository)) return
    this.set(repository, patch)
  }

  private set(repository: string, patch: Partial<RepoRunnerStatus>) {
    const current = this.statuses.get(repository) ?? { repository, state: 'idle', log: [], lastCommandAt: null }
    this.statuses.set(repository, { ...current, ...patch })
    this.emit()
  }

  private emit() {
    this.current = [...this.statuses.values()]
    for (const listener of this.listeners) listener()
  }

  /**
   * The prompt queue: every {@link QUEUE_POLL_MS}, the next prompt waiting for one of this machine's
   * repositories. A prompt for no repository in particular runs in the first one.
   *
   * Each lane — the person's first agent, their second… — runs one prompt at a time, and the lanes
   * run side by side: while some run, the loop keeps asking, telling the server which lanes are busy
   * so it hands out only prompts for the others. A run ending asks again at once.
   */
  private async queueLoop(settings: RunnerSettings, generation: number) {
    const { deps } = this
    const alive = () => generation === this.generation
    const headers = (key: string) => ({ 'Content-Type': 'application/json', 'X-DevPulse-Key': key, 'X-DevPulse-Provider': providerSlug(settings.agent) })
    const running = new Map<string, Promise<void>>()
    // Which queued prompt each busy lane is running, to end it when the person stops it.
    const held = new Map<string, string>()
    // A wait ends early when a run ends or when the server says a command was just queued (poke).
    const pause = () => {
      if (this.poked) {
        this.poked = false
        return Promise.resolve()
      }
      let wake: () => void = () => {}
      const woken = new Promise<void>((resolve) => {
        wake = () => {
          this.poked = false
          resolve()
        }
        this.wakers.add(wake)
      })
      return Promise.race([deps.sleep(running.size > 0 ? CANCEL_CHECK_MS : QUEUE_POLL_MS), woken, ...running.values()]).finally(() => {
        this.wakers.delete(wake)
      })
    }

    while (alive()) {
      if (held.size > 0) await this.endStopped(held, headers)
      if (running.size >= MAX_PARALLEL) {
        await Promise.race(running.values())
        continue
      }
      const key = await deps.key()
      if (!key) {
        await pause()
        continue
      }
      // Only repositories with a folder here: a prompt it took but could not run would be lost.
      const runnable = settings.repositories.filter((r) => deps.folderFor(r))
      if (runnable.length === 0) {
        await pause()
        continue
      }
      await this.undoAsked(key, headers)
      const query = new URLSearchParams({ machine: deps.machineId, repositories: runnable.join(',') })
      if (running.size > 0) query.set('busy', [...running.keys()].join(','))
      // What this machine is really running: one the server thinks is Running here but is not (the
      // app restarted mid-run) goes back to wait and runs again.
      // '-' when nothing: an empty value reaches the server as no value at all, the older app's way.
      query.set('holding', held.size > 0 ? [...held.keys()].join(',') : '-')
      const name = deps.machineName ? await deps.machineName().catch(() => null) : null
      if (name) query.set('machineName', name)
      let item: QueueItem | null = null
      try {
        const response = await deps.fetch(`${deps.apiBase}/prompt-queue/next?${query}`, { headers: headers(key) })
        if (response.status === 200) item = (await response.json()) as QueueItem
      } catch {
        // Offline: ask again at the next turn.
      }
      if (!alive()) return
      if (!item?.id || !item.text) {
        await pause()
        continue
      }

      const taken = item
      const lane = taken.lane && taken.lane > 1 ? taken.lane : 1
      // Sent with no project (docs/refactor/29): this computer decides where — never a project picked at random.
      // A work order's step with no project (Iniciar projeto) is Vephus's own, meant to run anywhere: as before.
      if (!taken.repository && !taken.workOrderId) {
        const busyKey = `?:${taken.id}`
        held.set(taken.id, busyKey)
        const job = this.runUnplaced(taken, runnable, lane, key, headers, alive).finally(() => {
          running.delete(busyKey)
          held.delete(taken.id)
        })
        running.set(busyKey, job)
        continue
      }
      const repository = runnable.find((r) => r.toLowerCase() === taken.repository?.toLowerCase()) ?? runnable[0]!
      const busyKey = laneKey(repository, lane)
      held.set(taken.id, busyKey)
      const job = this.runQueued(taken, repository, lane, key, headers, alive).finally(() => {
        running.delete(busyKey)
        held.delete(taken.id)
      })
      running.set(busyKey, job)
    }
  }

  /**
   * The prompts this machine is running that the person stopped (from the dock, here or on the web):
   * their agents end. Asked with the agent key; nothing happens when the API cannot be reached.
   */
  private async endStopped(held: Map<string, string>, headers: (key: string) => Record<string, string>) {
    const key = await this.deps.key()
    if (!key) return
    try {
      const response = await this.deps.fetch(`${this.deps.apiBase}/prompt-queue`, { headers: headers(key) })
      if (!response.ok) return
      const queue = (await response.json()) as { items?: Array<{ id: string; status: string }> }
      for (const item of queue.items ?? []) {
        const busyKey = held.get(item.id)
        if (busyKey && item.status === 'Cancelled') this.stops.get(busyKey)?.()
      }
    } catch {
      // Offline: asked again at the next check.
    }
  }

  /**
   * "Desfazer este turno" asked from the web or a phone (docs/refactor/02): this machine ran the turn and holds its
   * checkpoint, so it puts the folder back and says how it went. A folder with an agent at work waits for the next ask.
   */
  private async undoAsked(key: string, headers: (key: string) => Record<string, string>) {
    const { deps } = this
    if (!deps.restore) return
    let asked: Array<{ id: string; repository: string | null; checkpoint: string; files?: string[] | null }> = []
    try {
      const response = await deps.fetch(`${deps.apiBase}/prompt-queue/undo?${new URLSearchParams({ machine: deps.machineId })}`, { headers: headers(key) })
      const body: unknown = response.ok ? await response.json() : null
      if (Array.isArray(body)) asked = body as typeof asked
    } catch {
      return
    }
    for (const undo of asked) {
      // The folder the turn ran in: an extra agent's worktree, or the repository's own folder.
      const folder = checkpointFolder(undo.checkpoint) ?? (undo.repository ? deps.folderFor(undo.repository) : null)
      if (folder && this.busy(folder)) continue
      let body: { ok: boolean; restored?: number; error?: string }
      if (!folder) body = { ok: false, error: `The desktop app has no folder for ${undo.repository ?? 'this project'}.` }
      else {
        try {
          // Only the turn's own files: another agent may have worked in the folder since, or alongside it.
          if (undo.files && undo.files.length > 0 && deps.restoreFile) {
            for (const path of undo.files) await deps.restoreFile(folder, undo.checkpoint, path)
            body = { ok: true, restored: undo.files.length }
          } else body = { ok: true, restored: await deps.restore(folder, undo.checkpoint) }
        } catch (error) {
          body = { ok: false, error: String(error).replace(/^Error:\s*/, '').slice(0, 280) }
        }
      }
      await this.send(`${deps.apiBase}/prompt-queue/${undo.id}/undone`, headers(key), body)
    }
  }

  /** One queued prompt, run in its lane and closed. */
  /**
   * A command with no project (docs/refactor/29): the project its words name, else the one this computer's agent
   * picks; it runs there, and that project's window comes up. When none fits, the person is told, with the projects
   * this computer has — it never runs in a project picked at random.
   */
  private async runUnplaced(item: QueueItem, projects: string[], lane: number, key: string, headers: (key: string) => Record<string, string>, alive: () => boolean) {
    const words = item.prompt ?? item.text
    let chosen: string | null = projectNamedIn(words, projects)
    if (!chosen) {
      const answer = await this.ask(whichProjectQuestion(words, projects), this.deps.folderFor(projects[0]!) ?? '', item.agent)
      // The agent could not answer (not signed in, say): with one project here, the run itself tells what is wrong.
      const picked = answer === null ? (projects.length === 1 ? projects[0]! : null) : projectAnswered(answer, projects)
      chosen = picked === 'any' ? (projects.length === 1 ? projects[0]! : this.lastProject ?? projects[0]!) : picked
    }
    if (!chosen) {
      const summary = `Nenhum projeto deste computador serve para este pedido. Projetos aqui: ${projects.join(', ')}.\nDEVPULSE-STATUS: PENDING — mande de novo dizendo o projeto (ou abra o projeto neste computador)`
      await this.send(`${this.deps.apiBase}/prompt-queue/${item.id}/complete`, headers(key), { status: 'pending', summary, byDesktop: true })
      return
    }
    await this.send(`${this.deps.apiBase}/prompt-queue/${item.id}/project`, headers(key), { repository: chosen })
    this.deps.openProject?.(chosen)
    await this.runQueued(item, chosen, lane, key, headers, alive)
  }

  /** The project the last command run here was in: where a general request with no project goes. */
  private lastProject: string | null = null

  /** Asks the computer's agent one short question, without a conversation or a report; null when it could not. */
  private async ask(question: string, folder: string, agent?: string | null): Promise<string | null> {
    if (!folder) return null
    try {
      const handle = this.deps.run(question, folder, () => {}, isLocalAgent(agent) ? { agent } : {})
      const timer = setTimeout(() => handle.stop(), 90_000)
      const done = await handle.done.finally(() => clearTimeout(timer))
      return done.code === 0 ? done.output : null
    } catch {
      return null
    }
  }

  private async runQueued(item: QueueItem, repository: string, lane: number, key: string, headers: (key: string) => Record<string, string>, alive: () => boolean) {
    const { deps } = this
    const folder = deps.folderFor(repository)
    this.lastProject = repository
    let summary: string
    let status: 'done' | 'failed'
    let sessionId: string | undefined
    let undo: { checkpoint?: string; changes?: Array<{ path: string; status: string; added: number; deleted: number }> } = {}
    if (isUnpublishRequest(item.prompt ?? item.text) && deps.unpublish) {
      // "Parar de compartilhar" from the web (docs/refactor/33): the app closes its links; no agent is started.
      const closed = await deps.unpublish().catch(() => -1)
      summary = closed < 0 ? 'The links could not be closed on this computer.' : `DEVPULSE-UNPUBLISHED: ${closed}`
      status = closed < 0 ? 'failed' : 'done'
    } else if (!folder) {
      summary = `The desktop app has no folder for ${repository}.\nDEVPULSE-STATUS: PENDING — pick the repository's folder in the desktop app`
      status = 'failed'
    } else {
      // A command from elsewhere is a fresh agent of its own, streamed and reported like a prompt given here.
      const result = await this.execute(repository, folder, item.text, item.id, lane, {
        conversation: { id: sessionNameFor(item.id), resume: false },
        reported: Boolean(deps.reporter),
        ...(isLocalAgent(item.agent) ? { agent: item.agent } : {}),
        ...(item.sentBy ? { fromTeammate: item.sentBy } : {}),
      }, item.prompt)
      summary = replyFrom(result.output, result.code)
      status = result.code === 0 ? 'done' : 'failed'
      sessionId = result.sessionId
      // Paths and numbers, and the checkpoint's id: "Desfazer este turno" from the web (docs/refactor/02). Never the diff.
      if (result.checkpoint && result.changes && result.changes.length > 0) {
        undo = {
          checkpoint: result.checkpoint,
          changes: result.changes.map((c) => ({ path: c.path, status: c.status, added: c.added ?? 0, deleted: c.deleted ?? 0 })),
        }
      }
    }

    // Closes it only if the agent did not already, through the MCP; the server keeps the first close.
    // Retried while the API is unreachable: a result lost here leaves the step running for hours.
    // The agent's session id ends its sessions too: the agent is gone.
    await this.send(`${deps.apiBase}/prompt-queue/${item.id}/complete`, headers(key), { status, summary, byDesktop: true, ...(sessionId ? { clientSessionId: sessionId } : {}), ...undo })
    if (alive()) this.set(laneKey(repository, lane), { state: lane > 1 ? 'idle' : 'listening' })
  }

  /**
   * Runs one prompt in a fresh agent in the folder, after whatever is running there ends, and keeps
   * what it said and which files it touched for the screen.
   */
  private execute(repository: string, folder: string, prompt: string, queueId?: string, lane = 1, options: RunOptions = {}, shown?: string): Promise<RunOutcome> {
    // The first agent shares the folder's lock with remote commands and the chat; each other lane has its own.
    const lock = lane > 1 ? `${folder}#${lane}` : folder
    // Behind another run in this folder: say so at once, or the person sees nothing happen. The run on
    // screen stays there; only a lane showing nothing shows the waiting prompt instead.
    const key = laneKey(repository, lane)
    const text = shown ?? prompt
    const turn = { id: crypto.randomUUID(), text, mine: !queueId }
    if (this.locks.has(lock)) {
      const now = this.statuses.get(key)
      const waiting = [...(now?.waiting ?? []), turn]
      this.set(key, now?.state === 'working' ? { repository, lane, waiting } : { repository, lane, waiting, waitingForFolder: true, stream: [], streamPrompt: text })
    }
    return this.exclusive(lock, async () => {
      const waiting = this.statuses.get(key)?.waiting ?? []
      const stillThere = waiting.some((w) => w.id === turn.id)
      if (stillThere) this.set(key, { waiting: waiting.filter((w) => w.id !== turn.id) })
      // Taken back while it waited (withdraw): it never runs.
      if (this.withdrawn.delete(turn.id)) return { output: '', code: null, withdrawn: true }
      // Agent 2, 3… work in their own worktree (ADR-0011): never two agents editing one folder.
      const where = lane > 1 ? await this.laneFolder(repository, folder, lane) : folder
      let outcome = await this.executeNow(repository, where, prompt, queueId, lane, options, shown)
      // A limit hands the same request to the next agent of the person's order; each attempt is a turn
      // of its own (its own checkpoint, its own undo). Claude's conversation does not travel with it.
      let ran = options.agent ?? this.deps.agent?.()
      const order = this.deps.failover?.() ?? []
      const tried = new Set<LocalAgentId>(ran ? [ran] : [])
      const handoffs: string[] = []
      while (ran && order.length > 0 && limitHit(outcome.output)) {
        const next = nextAgent(order, tried)
        if (!next) break
        handoffs.push(`${AGENT_NAMES[ran] ?? ran} atingiu o limite do plano — ${AGENT_NAMES[next] ?? next} assumiu o pedido.`)
        tried.add(next)
        outcome = await this.executeNow(repository, where, prompt, queueId, lane, { ...options, agent: next, conversation: undefined }, shown)
        ran = next
      }
      if (handoffs.length > 0) outcome = { ...outcome, output: `${handoffs.join('\n')}\n\n${outcome.output}` }
      if (where !== folder && this.deps.worktreeCommit) {
        const title = (shown ?? prompt).split('\n').find((line) => line.trim())?.trim().slice(0, 120) ?? ''
        const commit = await this.deps.worktreeCommit(where, `Agente ${lane}: ${title}`).catch(() => null)
        const note = commit ? await this.bringBack(repository, folder, lane, commit) : null
        if (note) outcome = { ...outcome, output: `${outcome.output}\n\n${note}` }
      }
      return outcome
    })
  }

  /** Lanes whose work stopped coming into the person's folder: one turn did not fit, so the rest waits on the branch. */
  private readonly separated = new Set<string>()

  /**
   * An extra agent's turn comes into the person's folder by itself, as edits they have not committed — as agent 1's do
   * (the owner, 24/10/2026; docs/refactor/55). When a turn would touch what the person changed, their folder is left
   * as it is, the turn stays on the agent's branch, and from then on that agent's work waits there: said in its answer.
   */
  private async bringBack(repository: string, folder: string, lane: number, commit: string): Promise<string | null> {
    if (!this.deps.worktreeBring) return null
    const key = laneKey(repository, lane)
    const branch = `vephus/agente-${lane}`
    if (this.separated.has(key)) return `O que o agente ${lane} fez segue na branch ${branch}, separado da sua pasta. Junte quando quiser.`
    const brought = await this.deps.worktreeBring(folder, lane, commit).catch(() => null)
    if (!brought) return null
    if (!brought.applied) {
      this.separated.add(key)
      const files = brought.files.slice(0, 5).join(', ')
      return `O agente ${lane} mexeu em arquivos que você também mudou (${files}). Para não estragar o que é seu, o trabalho dele ficou na branch ${branch}. Junte quando quiser.`
    }
    if (brought.files.length === 0) return null
    return `Juntei na sua pasta o que o agente ${lane} fez: ${brought.files.length} arquivo(s), sem commit. A branch ${branch} também guarda.`
  }

  /** Where agent `lane` works: its own worktree when the folder can have one; the person's folder otherwise, as before. */
  private async laneFolder(repository: string, folder: string, lane: number): Promise<string> {
    if (!this.deps.worktreeFor) return folder
    try {
      const worktree = await this.deps.worktreeFor(folder, lane)
      this.set(laneKey(repository, lane), { repository, lane, worktree })
      return worktree.path
    } catch {
      return folder
    }
  }

  private readonly withdrawn = new Set<string>()

  /** Takes back a request given here that still waits its turn (the × above the box): it will not run. */
  withdraw(repository: string, lane: number, id: string): boolean {
    const key = laneKey(repository, lane)
    const waiting = this.statuses.get(key)?.waiting ?? []
    const turn = waiting.find((w) => w.id === id && w.mine)
    if (!turn) return false
    this.withdrawn.add(id)
    this.set(key, { waiting: waiting.filter((w) => w.id !== id) })
    return true
  }

  /** Forgets a lane's last answer (the dock's "limpar"): the screen goes back to the empty state. */
  clearResult(repository: string, lane = 1) {
    this.set(laneKey(repository, lane), { result: undefined, stream: [], streamPrompt: undefined, log: [] })
  }

  /** Closing agent `lane` of a repository removes its checkout; what it did stays on its branch. */
  async discardLane(repository: string, lane: number): Promise<boolean> {
    const folder = this.deps.folderFor(repository)
    if (!folder || lane < 2 || !this.deps.worktreeDiscard) return false
    const done = await this.deps.worktreeDiscard(folder, lane).catch(() => false)
    if (done) {
      this.set(laneKey(repository, lane), { repository, lane, worktree: undefined })
      this.separated.delete(laneKey(repository, lane))
    }
    return done
  }

  /**
   * A prompt the person gave on this machine (rodar aqui): run at once, in this lane's conversation —
   * no queue, no round trip to the server first. The run streams to the screen, reports itself to
   * Vephus, and stops at once with the lane's ■. Rejects with 'no-folder' when the repository has no
   * folder here.
   */
  async runHere(repository: string, prompt: string, lane = 1, agent?: LocalAgentId, shown?: string): Promise<{ output: string; code: number | null; withdrawn?: boolean }> {
    // Another agent for one prompt ("@codex …") runs on its own, outside the lane's conversation:
    // that conversation is the chosen agent's, and resuming it with another would fail.
    if (agent) return this.runOnce(repository, prompt, lane, agent, shown)
    const folder = this.deps.folderFor(repository)
    if (!folder) throw new Error('no-folder')
    const key = laneKey(repository, lane)
    const conversation = this.conversations.get(key) ?? { id: crypto.randomUUID(), turns: 0, workSessionId: null }
    this.conversations.set(key, conversation)
    // The instructions (the project's, this machine's) and the skills go with every turn, from the
    // runner (docs/refactor/21-steering-local-e-compartilhado.md): not only the first.
    this.here.add(key)
    try {
      const result = await this.execute(repository, folder, prompt, undefined, lane, { conversation: { id: conversation.id, resume: conversation.turns > 0 }, reported: Boolean(this.deps.reporter), withProject: true }, shown ?? prompt)
      if (result.withdrawn) return result
      conversation.turns += 1
      conversation.workSessionId = result.workSessionId ?? conversation.workSessionId
      this.set(key, { inConversation: true })
      return result
    } finally {
      this.here.delete(key)
      if (this.statuses.get(key)?.state === 'working') this.set(key, { state: 'idle' })
    }
  }

  private async runOnce(repository: string, prompt: string, lane: number, agent: LocalAgentId, shown?: string): Promise<{ output: string; code: number | null }> {
    const folder = this.deps.folderFor(repository)
    if (!folder) throw new Error('no-folder')
    const key = laneKey(repository, lane)
    this.here.add(key)
    try {
      return await this.execute(repository, folder, prompt, undefined, lane, { agent, reported: Boolean(this.deps.reporter), withProject: true }, shown ?? prompt)
    } finally {
      this.here.delete(key)
      if (this.statuses.get(key)?.state === 'working') this.set(key, { state: 'idle' })
    }
  }

  /** Ends this lane's conversation: the next prompt starts a new agent, and Vephus closes the session. */
  newConversation(repository: string, lane = 1) {
    const key = laneKey(repository, lane)
    const conversation = this.conversations.get(key)
    this.conversations.delete(key)
    this.set(key, { inConversation: false, stream: [], streamPrompt: undefined })
    const { reporter, provider } = this.deps
    if (conversation?.workSessionId && reporter)
      void reporter.sendNow('work.completed', { summary: 'Conversation closed in the desktop app' }, provider?.() ?? 'claude-code', conversation.workSessionId)
  }

  /**
   * Posts a result back, retrying with growing pauses while the API is unreachable or failing (5xx).
   * A refusal (4xx) is final: retrying it would not change the answer.
   */
  private async send(url: string, headers: Record<string, string>, body: unknown): Promise<boolean> {
    const { deps } = this
    for (let attempt = 0; ; attempt++) {
      try {
        const response = await deps.fetch(url, { method: 'POST', headers, body: JSON.stringify(body) })
        if (response.ok || (response.status >= 400 && response.status < 500)) return response.ok
      } catch {
        // Unreachable: try again below.
      }
      const wait = SEND_RETRIES_MS[attempt]
      if (wait === undefined) return false
      await deps.sleep(wait)
    }
  }

  /**
   * Runs `work` when nothing else runs in `folder`: one agent at a time per folder, whoever starts it
   * — a queued prompt, a remote command, or the desktop chat.
   */
  async exclusive<T>(folder: string, work: () => Promise<T>): Promise<T> {
    const previous = this.locks.get(folder) ?? Promise.resolve()
    const mine = previous.catch(() => undefined).then(work)
    this.locks.set(folder, mine)
    try {
      return await mine
    } finally {
      if (this.locks.get(folder) === mine) this.locks.delete(folder)
    }
  }

  /** Stops the agent running for a lane of `repository` (the first by default), if one is: the ■ buttons. */
  stopRepository(repository: string, lane = 1): boolean {
    const stop = this.stops.get(laneKey(repository, lane))
    if (!stop) return false
    stop()
    return true
  }

  /** The runs at work in each folder now, and whether each shared the folder with another (docs/refactor/02). */
  private readonly inFolder = new Map<string, Set<{ shared: boolean }>>()

  /** Whether an agent is running (or waiting to run) in `folder` right now. */
  busy(folder: string): boolean {
    return this.locks.has(folder)
  }

  private async executeNow(repository: string, folder: string, prompt: string, queueId?: string, lane = 1, options: RunOptions = {}, shown?: string): Promise<RunOutcome> {
    const { deps } = this
    const key = laneKey(repository, lane)
    this.set(key, { repository, lane, state: 'working', waitingForFolder: false, lastCommandAt: new Date().toISOString(), log: [], stream: [], streamPrompt: shown ?? prompt })
    const before = deps.status ? await deps.status(folder).catch(() => []) : []
    // The folder as it was before this run: the run's changes are read against it, and undone to it.
    const checkpointName = `${(options.conversation?.id ?? queueId ?? 'run').replace(/[^A-Za-z0-9-]/g, '')}-${Date.now()}`.slice(0, 100)
    const checkpoint = deps.checkpoint ? await deps.checkpoint(folder, checkpointName).catch(() => null) : null
    if (checkpoint) rememberCheckpoint(checkpoint, folder)
    // Two agents in one folder (docs/refactor/02): each run notes the files its own tools write, and whether another
    // run shared the folder with it; then its changes are only its own.
    const here = { shared: false }
    const others = this.inFolder.get(folder) ?? new Set<{ shared: boolean }>()
    if (others.size > 0) {
      here.shared = true
      for (const other of others) other.shared = true
    }
    others.add(here)
    this.inFolder.set(folder, others)
    const written = new Set<string>()
    const log: string[] = []
    const stream: StreamItem[] = []
    const show = (item: StreamItem) => {
      // Consecutive text joins up, as the agent writes it; a tool is a line of its own.
      const last = stream.at(-1)
      if (item.kind === 'text' && last?.kind === 'text') stream[stream.length - 1] = { kind: 'text', text: `${last.text}\n\n${item.text}` }
      else stream.push(item)
      if (stream.length > STREAM_ITEMS) stream.shift()
      this.set(key, { stream: [...stream] })
    }
    const report = options.reported && deps.reporter ? new RunReport(deps.reporter, deps.provider?.() ?? 'claude-code', repository, options.conversation?.id ?? null) : null
    if (report) void report.started(shown ?? prompt, deps.branch ? await deps.branch(folder).catch(() => null) : null)

    // What the agent finally said, from the stream's last event; the text it wrote, as a fallback.
    let finalText: string | null = null
    const texts: string[] = []
    let streaming = false
    let parse = parseStreamLine
    const run = deps.run(prompt, folder, (line, origin) => {
      if (streaming) {
        for (const event of parse(line)) {
          if (event.kind === 'init') report?.model(event.model)
          if (event.kind === 'text') {
            texts.push(event.text)
            show({ kind: 'text', text: event.text })
            log.push(event.text)
            report?.said(event.text)
          }
          if (event.kind === 'tool') {
            if (WRITING_TOOLS.has(event.name) && event.detail) written.add(relativeTo(folder, event.detail))
            show({ kind: 'tool', name: event.name, detail: event.detail })
            log.push(`› ${event.name}${event.detail ? ` ${event.detail}` : ''}`)
            report?.tool(event.name, event.detail, event.command)
          }
          if (event.kind === 'result') {
            finalText = event.text || null
            report?.usage(event.usage)
          }
        }
      } else {
        // Plain output (an agent without a stream): its lines, as printed. What it prints on stderr — its
        // own warnings ("Sandbox disabled…") — goes to the Log only, not into the answer.
        log.push(line)
        if (origin === 'stderr') return
        const last = stream.at(-1)
        if (last?.kind === 'text') stream[stream.length - 1] = { kind: 'text', text: `${last.text}\n${line}` }
        else stream.push({ kind: 'text', text: line })
        this.set(key, { stream: [...stream] })
      }
      while (log.length > LOG_LINES) log.shift()
      this.set(key, { log: [...log] })
    }, options)
    streaming = run.streaming === true
    parse = run.parse ?? parseStreamLine
    this.stops.set(key, run.stop)

    let result: { output: string; code: number | null }
    let overtime = false
    const cap = setTimeout(() => {
      overtime = true
      run.stop()
    }, this.deps.maxRunMs ?? MAX_RUN_MS)
    try {
      result = await run.done
    } catch (error) {
      // The free local AI missing (localAgent.ts) is said in words, with where to install it.
      result = { output: /local-ai-not-installed/.test(String(error)) ? LOCAL_AI_MISSING : String(error), code: -1 }
    } finally {
      clearTimeout(cap)
    }
    if (overtime) {
      const minutes = Math.round((this.deps.maxRunMs ?? MAX_RUN_MS) / 60_000)
      result = { output: `${result.output}\n\nThe desktop app stopped the agent after ${minutes} minutes.`, code: null }
    }
    this.stops.delete(key)

    // A streamed run printed JSON events: what it said is the final message (where the status
    // lines and the fix prompt are), or the text it wrote when it ended without one.
    if (streaming) {
      const said = (finalText ?? texts.join('\n\n')).trim()
      const tail = result.code === 0 ? '' : result.output.split('\n').filter((l) => !l.trim().startsWith('{')).join('\n').trim()
      result = { output: [said, tail].filter(Boolean).join('\n\n'), code: result.code }
    }

    others.delete(here)
    if (others.size === 0) this.inFolder.delete(folder)
    const all = checkpoint && deps.changes ? await deps.changes(folder, checkpoint).catch(() => null) : null
    const changes = all && here.shared ? all.filter((c) => written.has(c.path)) : all
    const touched = changes
      ? { created: changes.filter((c) => c.status === 'A').map((c) => c.path), modified: changes.filter((c) => c.status !== 'A').map((c) => c.path) }
      : filesTouched(before, deps.status ? await deps.status(folder).catch(() => []) : [])
    this.set(key, {
      result: {
        queueId,
        prompt: shown ?? prompt,
        output: result.output,
        code: result.code,
        created: touched.created,
        modified: touched.modified,
        finishedAt: new Date().toISOString(),
        ...(checkpoint ? { checkpoint, folder } : {}),
        ...(changes ? { changes } : {}),
        ...(here.shared ? { shared: true } : {}),
      },
    })

    const lines = changes ? { added: changes.reduce((n, c) => n + (c.added ?? 0), 0), deleted: changes.reduce((n, c) => n + (c.deleted ?? 0), 0) } : null
    const workSessionId = report ? await report.ended(result.output, [...touched.created, ...touched.modified], lines) : null

    const address = localAddressToOpen(result.output)
    if (address && deps.open) deps.open(address)
    // Publicar: only when the person asked for it (the catalog prompt's marker, never the agent's say-so), the app
    // shares what the agent reported, and what it opened becomes part of the reply the web reads.
    let output = result.output
    if (deps.publish && !options.fromTeammate && isPublishRequest(prompt)) {
      const services = servicesIn(result.output)
      const outcome = services.length ? await deps.publish(services).catch(() => null) : null
      if (outcome) output = `${output}\n\n${publishedLines(outcome)}`
    }
    return { ...result, output, sessionId: run.sessionId, workSessionId, ...(checkpoint ? { checkpoint } : {}), ...(changes ? { changes } : {}) }
  }

  private async loop(repository: string, settings: RunnerSettings, generation: number) {
    const { deps } = this
    const alive = () => generation === this.generation
    const clientSessionId = sessionFor(deps.machineId, repository)

    while (alive()) {
      const folder = deps.folderFor(repository)
      if (!folder) {
        this.poll(repository, { state: 'no-folder' })
        await deps.sleep(30_000)
        continue
      }
      const key = await deps.key()
      if (!key) {
        this.poll(repository, { state: 'no-key' })
        await deps.sleep(30_000)
        continue
      }

      const headers = { 'Content-Type': 'application/json', 'X-DevPulse-Key': key, 'X-DevPulse-Provider': providerSlug(settings.agent) }
      // fresh: every command here is a new agent, so the server puts the pinned instructions before it.
      const query = new URLSearchParams({ clientSessionId, repository, wait: String(ASK_WAIT_SECONDS), fresh: 'true' })

      let response: Response
      try {
        response = await deps.fetch(`${deps.apiBase}/remote/next?${query}`, { headers })
      } catch {
        this.poll(repository, { state: 'offline' })
        await deps.sleep(10_000)
        continue
      }
      if (!alive()) return

      if (response.status === 204) {
        this.poll(repository, { state: 'listening' })
        continue
      }
      if (response.status === 423) {
        this.poll(repository, { state: 'remote-off' })
        await deps.sleep(30_000)
        continue
      }
      if (response.status === 401 || response.status === 403) {
        this.poll(repository, { state: 'key-problem' })
        await deps.sleep(60_000)
        continue
      }
      if (response.status !== 200) {
        this.poll(repository, { state: 'offline' })
        await deps.sleep(10_000)
        continue
      }

      const command = (await response.json()) as { text?: string; sentBy?: string | null }
      const text = command.text?.trim()
      if (!text) continue
      // A teammate's command (docs/refactor/63): the agent is told it is not its own person speaking, and the run is held
      // to editing the project's files.
      const sentBy = command.sentBy?.trim() || undefined
      const prompt = sentBy ? `${teammateNote(sentBy)}

${text}` : text

      const result = await this.execute(repository, folder, prompt, undefined, 1, sentBy ? { fromTeammate: sentBy } : {})

      // The reply goes back even if the runner was switched off meanwhile: the step is over
      // either way, and the order should hear how it ended.
      await this.send(`${deps.apiBase}/remote/replies`, headers, { clientSessionId, repository, text: replyFrom(result.output, result.code) })
      if (alive()) this.set(repository, { state: 'listening' })
    }
  }
}
